import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  Card,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Chip,
} from '@mui/material'
import { Search, Delete, Add, Visibility, Edit } from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchTrainingSeries,
  fetchCourses,
  fetchLinkedServices,
  selectTrainingSeries,
  selectTrainingSeriesStatus,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
} from '../manage.slice'
import { useLinkedServiceSelection } from '../hooks/use-linked-service-selection'
import { MIN_BUTTON_SIZE, TRAINING_SERIES_URLS } from '../constants'
import { deleteTrainingSeries } from '../utils/api'
import { TrainingSeriesWithStats } from './types'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'

export default function TrainingSeriesV4(): ReactElement {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()
  let linkedServiceID = useLinkedServiceSelection()

  let [searchQuery, setSearchQuery] = useState<string>('')
  let [selectedCategory, setSelectedCategory] = useState<string>('')
  let [seriesToDelete, setSeriesToDelete] =
    useState<TrainingSeriesWithStats | null>(null)
  let { showError, showSuccess, NotificationComponent } = useNotifications()
  let [filterResetMessage, setFilterResetMessage] = useState<string>('')
  let [isDeleting, setIsDeleting] = useState<boolean>(false)

  let trainingSeries = useAppSelector(selectTrainingSeries)
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useAutoFetch(trainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let resetFilters = useCallback(() => {
    setSearchQuery('')
    setSelectedCategory('')
    setFilterResetMessage('Filters have been reset')
    setTimeout(() => setFilterResetMessage(''), 3000)
  }, [])

  useEffect(() => {
    resetFilters()
  }, [linkedServiceID, resetFilters])

  useEffect(() => {
    let state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      showSuccess(state.successMessage)
      // Clear the state to prevent message showing on refresh
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, showSuccess])

  let trainingSeriesWithStats: TrainingSeriesWithStats[] = useMemo(
    () =>
      trainingSeries.map((ts) => {
        let seriesCourses = courses.filter((c) => ts.courseIDs.includes(c.id))
        let totalHours = seriesCourses.reduce(
          (sum, course) => sum + (course.length || 0),
          0,
        )
        let categories = Array.from(
          new Set(seriesCourses.map((c) => c.section || 'Uncategorized')),
        )

        return {
          ...ts,
          courseCount: seriesCourses.length,
          totalHours,
          categories,
        }
      }),
    [trainingSeries, courses],
  )

  // Get distinct categories for filter dropdown
  let availableCategories = useMemo(() => {
    if (!linkedServiceID) return []

    let categoriesSet = new Set<string>()
    trainingSeriesWithStats
      .filter((ts) => ts.linkedServiceID === linkedServiceID)
      .forEach((ts) => {
        ts.categories.forEach((cat) => categoriesSet.add(cat))
      })

    return Array.from(categoriesSet).toSorted((a, b) => a.localeCompare(b))
  }, [trainingSeriesWithStats, linkedServiceID])

  // Filter by linked service, category, and search query (optimized single pass)
  let filteredTrainingSeries = useMemo(() => {
    if (!linkedServiceID) return []

    let query = searchQuery.toLowerCase()
    return trainingSeriesWithStats.filter((ts) => {
      // Check linked service
      if (ts.linkedServiceID !== linkedServiceID) return false
      // Check category
      if (selectedCategory && !ts.categories.includes(selectedCategory))
        return false
      // Check search query
      if (searchQuery && !ts.name.toLowerCase().includes(query)) return false
      return true
    })
  }, [trainingSeriesWithStats, linkedServiceID, selectedCategory, searchQuery])

  let stats = useMemo(() => {
    let totalForService = trainingSeriesWithStats.filter(
      (ts) => ts.linkedServiceID === linkedServiceID,
    )

    return {
      totalCount: totalForService.length,
      filteredCount: filteredTrainingSeries.length,
      isFiltered: searchQuery !== '' || selectedCategory !== '',
      hasTrainingSeries: totalForService.length > 0,
      totalCourses: filteredTrainingSeries.reduce(
        (sum, ts) => sum + ts.courseCount,
        0,
      ),
      totalHours: filteredTrainingSeries.reduce(
        (sum, ts) => sum + ts.totalHours,
        0,
      ),
    }
  }, [
    filteredTrainingSeries,
    trainingSeriesWithStats,
    linkedServiceID,
    searchQuery,
    selectedCategory,
  ])

  let isLoading =
    trainingSeriesStatus === 'pending' ||
    coursesStatus === 'pending' ||
    linkedServicesStatus === 'pending'

  let handleViewSeries = useCallback(
    (seriesId: string) => {
      navigate(`${TRAINING_SERIES_URLS.INDEX}/${seriesId}`)
    },
    [navigate],
  )

  let handleCreateNew = useCallback(() => {
    navigate(TRAINING_SERIES_URLS.CREATE, {
      state: { linkedServiceID },
    })
  }, [navigate, linkedServiceID])

  let handleDeleteTrainingSeries = useCallback(async () => {
    if (!seriesToDelete) return

    setIsDeleting(true)

    await deleteTrainingSeries(seriesToDelete.id)
      .then(() => dispatch(fetchTrainingSeries()))
      .then(() => {
        showSuccess(
          `Training series "${seriesToDelete.name}" deleted successfully`,
        )
      })
      .catch((err) => {
        let errorMessage = `Error deleting training series "${seriesToDelete.name}".`
        // eslint-disable-next-line no-console
        console.error(
          errorMessage,
          'Training Series ID: ',
          seriesToDelete.id,
          'Error: ',
          err,
        )
        showError(errorMessage)
      })
      .finally(() => {
        setIsDeleting(false)
        setSeriesToDelete(null)
      })
  }, [seriesToDelete, dispatch, showError, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" className="mb-8">
        Training Series
      </Typography>
      <Card className="p-6 mb-6">
        <Box className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-center">
          <FormControl fullWidth>
            <InputLabel id="linked-service-label">Linked Service</InputLabel>
            <Select
              labelId="linked-service-label"
              value={linkedServiceID || ''}
              onChange={(e) =>
                dispatch(activeLinkedServiceChanged(e.target.value))
              }
              label="Linked Service"
              aria-label="Select linked service"
              sx={{ backgroundColor: 'background.paper' }}
            >
              <MenuItem value="">
                <em>Select a linked service</em>
              </MenuItem>
              {linkedServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Search Training Series"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <Search className="mr-2" />,
              },
            }}
            disabled={!linkedServiceID || !stats.hasTrainingSeries}
            aria-label="Search training series by name"
            sx={{
              '& .MuiInputBase-root': { backgroundColor: 'background.paper' },
            }}
          />

          <FormControl fullWidth>
            <InputLabel id="category-filter-label">Category</InputLabel>
            <Select
              labelId="category-filter-label"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
              aria-label="Filter by category"
              sx={{ backgroundColor: 'background.paper' }}
              disabled={!linkedServiceID || !stats.hasTrainingSeries}
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {availableCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>
      <Paper className="p-6">
        <Box className="flex flex-wrap items-center justify-between mb-6">
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            disabled={!linkedServiceID}
            onClick={handleCreateNew}
            aria-label="Create new training series"
            className="mb-4 md:mb-0 basis-full md:basis-auto"
          >
            Create Training Series
          </Button>
          <Box
            className="flex items-center justify-end gap-2 ml-4 grow"
            role="status"
            aria-live="polite"
            aria-label="Training series statistics"
          >
            <Chip
              size="medium"
              label={`${stats.totalCount} series`}
              color="primary"
              aria-label={`Total: ${stats.totalCount} training series`}
            />
            {stats.isFiltered && (
              <>
                <Chip
                  size="medium"
                  label={`${stats.filteredCount} of ${stats.totalCount} shown`}
                  color="secondary"
                  variant="outlined"
                  aria-label={`Showing ${stats.filteredCount} of ${stats.totalCount} training series after filtering`}
                />

                <Button
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  onClick={resetFilters}
                  className="ml-4 whitespace-nowrap"
                >
                  Reset Filters
                </Button>
              </>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box
            className="flex justify-center py-16"
            role="status"
            aria-live="polite"
            aria-label="Loading training series"
          >
            <CircularProgress aria-label="Loading" />
          </Box>
        ) : !linkedServiceID ? (
          <Box className="flex justify-center py-16" role="status">
            <Typography variant="body1" color="text.secondary">
              Please select a linked service to view training series
            </Typography>
          </Box>
        ) : filteredTrainingSeries.length === 0 ? (
          <Box className="flex justify-center py-16" role="status">
            <Typography variant="body1" color="text.secondary">
              No training series found for this linked service
            </Typography>
          </Box>
        ) : (
          <TableContainer className="overflow-x-auto">
            <Table sx={{ minWidth: 650 }} aria-label="Training series table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Courses</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTrainingSeries.map((series) => (
                  <TableRow
                    key={series.id}
                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell className="whitespace-nowrap">
                      {series.name}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {series.categories.length > 0
                        ? series.categories.join(', ')
                        : 'No categories'}
                    </TableCell>
                    <TableCell>{series.courseCount}</TableCell>
                    <TableCell>{series.totalHours.toFixed(1)}</TableCell>
                    <TableCell align="right" className="whitespace-nowrap">
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() => handleViewSeries(series.id)}
                          aria-label={`View ${series.name}`}
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <Visibility color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() =>
                            navigate(
                              `${TRAINING_SERIES_URLS.INDEX}/${series.id}/edit`,
                              {
                                state: { edit: true },
                              },
                            )
                          }
                          aria-label={`Edit ${series.name}`}
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <Edit color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => setSeriesToDelete(series)}
                          aria-label={`Delete ${series.name}`}
                          color="error"
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <DeleteConfirmationModal
        isOpen={!!seriesToDelete}
        onClose={() => setSeriesToDelete(null)}
        onConfirm={handleDeleteTrainingSeries}
        entityName="training series"
        entityDisplayName={seriesToDelete?.name || ''}
        isSubmitting={isDeleting}
      />
      {/* Screen reader announcements */}
      {filterResetMessage && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          {filterResetMessage}
        </Box>
      )}
      {NotificationComponent}
    </Container>
  )
}
